import { object, string, array } from '@calendly/form/validation'
import { I18n } from '@calendly/i18n'
import { NAME_FORMATS } from '@calendly/routing-form-landing'

import {
  Name,
  Text,
  Email,
  Phone,
  Textarea,
  Select,
  Radios
} from 'features/routing/forms/edit_page/fields_component/fields_section/field_preview'

// i18n-tasks-use t('publisher.routing.forms.edit.intake_form.field_default_labels.#{field}')
export const fieldsConfig = {
  name: {
    menuIcon: 'person-fill',
    isUsedForRouting: true,
    preview: Name,
    defaultLabelKey: 'name',
    operators: [
      'equals',
      'does_not_equal',
      'contains',
      'does_not_contain',
      'starts_with',
      'has_value',
      'has_no_value'
    ],
  },
  email: {
    menuIcon: 'mail',
    isUsedForRouting: true,
    operators: [
      'email_domain_contains',
      'email_domain_does_not_contain',
      'has_value',
      'has_no_value'
    ],
    preview: Email,
    defaultLabelKey: 'email'
  },
  phone: {
    menuIcon: 'phone',
    isUsedForRouting: false,
    preview: Phone,
    defaultLabelKey: 'phone'
  },
  select: {
    menuIcon: 'arrow-down-fill',
    isUsedForRouting: true,
    operators: [
      'equals',
      'does_not_equal'
    ],
    hasSelectableOptions: true,
    preview: Select,
  },
  radios: {
    menuIcon: 'radio',
    isUsedForRouting: true,
    operators: [
      'equals',
      'does_not_equal'
    ],
    hasSelectableOptions: true,
    preview: Radios,
  },
  text: {
    menuIcon: 'one-line',
    isUsedForRouting: true,
    operators: [
      'equals',
      'does_not_equal',
      'contains',
      'does_not_contain',
      'starts_with',
      'greater_than',
      'greater_than_inclusive',
      'less_than',
      'less_than_inclusive',
      'has_value',
      'has_no_value'
    ],
    preview: Text
  },
  textarea: {
    menuIcon: 'multiple-line',
    isUsedForRouting: true,
    preview: Textarea,
    operators: [
      'equals',
      'does_not_equal',
      'contains',
      'does_not_contain',
      'starts_with',
      'has_value',
      'has_no_value'
    ],
  },
}

export const getInitialFieldFormState = (kind) => {
  let options = null
  const { defaultLabelKey, hasSelectableOptions } = fieldsConfig[kind]

  if (hasSelectableOptions) {
    options = { selectableOptions: [{ value: '' }, { value: '' }, { value: '' }] }
  }

  if (kind === 'name') {
    options = {
      nameFormat: NAME_FORMATS.combined,
      firstNameLabel: I18n.t('publisher.routing.forms.edit.intake_form.field_default_labels.first_name'),
      lastNameLabel: I18n.t('publisher.routing.forms.edit.intake_form.field_default_labels.last_name')
    }
  }

  const label = defaultLabelKey
    ? I18n.t(defaultLabelKey, { scope: 'publisher.routing.forms.edit.intake_form.field_default_labels' })
    : ''

  return {
    kind,
    label,
    required: false,
    options
  }
}

export const MIN_SELECTABLE_OPTIONS = 1
export const MAX_SELECTABLE_OPTIONS = {
  radios: 200,
  select: 300
}

const getSchemaForOptions = (kind) => {
  if (kind === 'name') {
    return object().shape({
      nameFormat: string().required(),
      firstNameLabel: string().when('nameFormat', {
        is: (nameFormat) => nameFormat === NAME_FORMATS.separate,
        then: string().required()
      }),
      lastNameLabel: string().when('nameFormat', {
        is: (nameFormat) => nameFormat === NAME_FORMATS.separate,
        then: string().required()
      }),
    })
  }

  return fieldsConfig[kind].hasSelectableOptions && object().shape({
    selectableOptions: array()
      .of(
        object().shape({
          value: string().trim().max(255).required()
        })
      )
      .min(
        MIN_SELECTABLE_OPTIONS,
        I18n.t('publisher.routing.forms.edit.errors.selectable_options.min', { min: MIN_SELECTABLE_OPTIONS })
      )
      .max(
        MAX_SELECTABLE_OPTIONS[kind],
        I18n.t('publisher.routing.forms.edit.errors.selectable_options.max', { max: MAX_SELECTABLE_OPTIONS[kind] })
      )
  })
}

export const buildValidationForFieldForm = (kind) => object().shape({
  label: string().trim().max(255).required(),
  options: getSchemaForOptions(kind)
})

// i18n-tasks-use t('publisher.routing.forms.edit.intake_form.fields.name')
// i18n-tasks-use t('publisher.routing.forms.edit.intake_form.fields.email')
// i18n-tasks-use t('publisher.routing.forms.edit.intake_form.fields.phone')
// i18n-tasks-use t('publisher.routing.forms.edit.intake_form.fields.select')
// i18n-tasks-use t('publisher.routing.forms.edit.intake_form.fields.radios')
// i18n-tasks-use t('publisher.routing.forms.edit.intake_form.fields.text')
// i18n-tasks-use t('publisher.routing.forms.edit.intake_form.fields.textarea')
export const getFieldKindLabel = (kind) => I18n.t(`publisher.routing.forms.edit.intake_form.fields.${kind}`)

// i18n-tasks-use t('publisher.routing.forms.edit.intake_form.fields.first_name')
// i18n-tasks-use t('publisher.routing.forms.edit.intake_form.fields.last_name')
export const getLabelForSeparateName = (type) => I18n.t(`publisher.routing.forms.edit.intake_form.fields.${type}_name`)

export const isFieldKind = (value) => !!fieldsConfig[value]
